import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, X } from 'lucide-react';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  blockedDates?: string[];
}

export function DateRangePicker({ startDate, endDate, onDateChange, blockedDates = [] }: DateRangePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  useEffect(() => {
    if (startDate) {
      setCurrentMonth(new Date(startDate));
    }
  }, []);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  // Format date to YYYY-MM-DD string using local time
  // IMPORTANT: This must match the formatDateToString in DetailPage.tsx
  const formatDateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateBlocked = (date: Date): boolean => {
    const dateStr = formatDateToString(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate < today || blockedDates.includes(dateStr);
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    const dateStr = formatDateToString(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isDateInHoverRange = (date: Date): boolean => {
    if (!startDate || !hoveredDate || selectingStart) return false;
    const dateStr = formatDateToString(date);
    const rangeStart = startDate < hoveredDate ? startDate : hoveredDate;
    const rangeEnd = startDate < hoveredDate ? hoveredDate : startDate;
    return dateStr > rangeStart && dateStr < rangeEnd;
  };

  const hasBlockedDateInRange = (start: string, end: string): boolean => {
    const startD = new Date(start);
    const endD = new Date(end);

    for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
      if (blockedDates.includes(formatDateToString(d))) {
        return true;
      }
    }
    return false;
  };

  const handleDateClick = (date: Date) => {
    if (isDateBlocked(date)) return;

    const dateStr = formatDateToString(date);

    if (selectingStart) {
      onDateChange(dateStr, '');
      setSelectingStart(false);
    } else {
      if (dateStr < startDate) {
        onDateChange(dateStr, startDate);
        setShowCalendar(false);
        setSelectingStart(true);
      } else {
        if (hasBlockedDateInRange(startDate, dateStr)) {
          return;
        }
        onDateChange(startDate, dateStr);
        setShowCalendar(false);
        setSelectingStart(true);
      }
    }
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onDateChange('', '');
    setSelectingStart(true);
    setHoveredDate(null);
  };

  const goToPreviousMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getDuration = (): number => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long'
  });

  const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const duration = getDuration();

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Dates de séjour
      </label>
      <button
        type="button"
        onClick={() => setShowCalendar(!showCalendar)}
        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white flex items-center justify-between"
      >
        <div className="flex items-center space-x-2">
          <Calendar className="text-gray-500" size={20} />
          {startDate && endDate ? (
            <span className="text-gray-800">
              {formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}
              <span className="ml-2 text-sm text-gray-500">({duration} {duration > 1 ? 'nuits' : 'nuit'})</span>
            </span>
          ) : startDate ? (
            <span className="text-gray-600">
              {formatDisplayDate(startDate)} - <span className="text-gray-500">Date de départ</span>
            </span>
          ) : (
            <span className="text-gray-500">Sélectionner les dates</span>
          )}
        </div>
        {(startDate || endDate) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label="Effacer les dates"
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={16} className="text-gray-500" />
          </button>
        )}
      </button>

      {showCalendar && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setShowCalendar(false);
              setSelectingStart(true);
            }}
          />
          <div className="absolute z-20 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-full max-w-[calc(100vw-2rem)] md:max-w-96 md:w-96 left-0 right-0 md:right-auto md:left-auto">
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">
                {selectingStart
                  ? "1. Sélectionnez la date d'arrivée"
                  : "2. Sélectionnez la date de départ"}
              </p>
            </div>

            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={goToPreviousMonth}
                aria-label="Mois précédent"
                className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="font-semibold text-gray-800 capitalize">
                {monthYear}
              </div>
              <button
                type="button"
                onClick={goToNextMonth}
                aria-label="Mois suivant"
                className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="aspect-square" />;
                }

                const dateStr = formatDateToString(date);
                const isStart = dateStr === startDate;
                const isEnd = dateStr === endDate;
                const isInRange = isDateInRange(date);
                const isInHover = isDateInHoverRange(date);
                const isBlocked = isDateBlocked(date);
                const today = new Date();
                const isToday = dateStr === formatDateToString(today);

                let className = 'aspect-square flex items-center justify-center text-sm rounded-lg transition cursor-pointer min-h-[44px] ';

                if (isBlocked) {
                  className += 'bg-red-50 text-red-300 cursor-not-allowed line-through';
                } else if (isStart || isEnd) {
                  className += 'bg-blue-600 text-white font-bold';
                } else if (isInRange) {
                  className += 'bg-blue-100 text-blue-800 font-medium';
                } else if (isInHover) {
                  className += 'bg-blue-50 text-blue-600';
                } else if (isToday) {
                  className += 'bg-gray-100 text-gray-900 font-semibold ring-2 ring-blue-400';
                } else {
                  className += 'hover:bg-gray-100 text-gray-700';
                }

                return (
                  <button
                    key={dateStr}
                    type="button"
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={() => setHoveredDate(dateStr)}
                    onMouseLeave={() => setHoveredDate(null)}
                    disabled={isBlocked}
                    className={className}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-600 rounded mr-2"></div>
                  <span className="text-gray-600">Sélectionné</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-50 rounded mr-2"></div>
                  <span className="text-gray-600">Indisponible</span>
                </div>
              </div>

              {startDate && !endDate && (
                <button
                  type="button"
                  onClick={(e) => handleClear(e)}
                  className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Recommencer
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
